import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, Package, Download, CheckCircle, Truck, Shield, 
  Briefcase, Building2, ClipboardCheck, Headphones, Star, ArrowRight,
  GraduationCap, Eye
} from 'lucide-react';
import SEO from '../components/shared/SEO';

// Template Packet Data
const TEMPLATE_PACKETS = [
  {
    id: 'cdl-success',
    title: 'CDL Success Packet',
    description: 'Everything you need to ace your CDL exam and start your trucking career.',
    icon: GraduationCap,
    color: '#D4AF37',
    price: 25,
    items: [
      'Pre-Trip Inspection Checklist',
      'CDL Study Planner',
      'CDL Test Prep Cheat Sheet',
      'Air Brakes Quick Guide',
      'Backing Maneuver Guide',
      'Road Test Tips Sheet',
      'CDL Glossary'
    ],
    audience: 'CDL Students'
  },
  {
    id: 'trucking-operations',
    title: 'Trucking Operations Packet',
    description: 'Track your revenue, expenses, and KPIs like a professional owner-operator.',
    icon: Truck,
    color: '#C0C0C0',
    price: 25,
    items: [
      'Cost Per Mile Calculator',
      'Load Profit Calculator',
      'Maintenance Tracker',
      'Fuel Log Tracker',
      'Weekly Revenue Tracker',
      'Expense Tracker',
      'KPI Tracking Sheet'
    ],
    audience: 'Owner Operators'
  },
  {
    id: 'freight-broker',
    title: 'Freight Broker Packet',
    description: 'Scripts, templates, and tools to land shippers and manage carriers.',
    icon: Briefcase,
    color: '#D4AF37',
    price: 25,
    items: [
      'Shipper Outreach Email Templates',
      'Cold Call Script for Shippers',
      'Carrier Vetting Checklist',
      'Broker-Carrier Agreement Template',
      'Load Tracking Sheet',
      'Profit/Margin Calculator',
      'Follow-Up Email Templates'
    ],
    audience: 'Freight Brokers'
  },
  {
    id: 'authority-builder',
    title: 'Authority Builder Packet',
    description: 'Step-by-step guides and checklists to launch your trucking authority.',
    icon: Building2,
    color: '#CD7F32',
    price: 25,
    items: [
      '90-Day Startup Checklist',
      'Authority Setup Checklist (MC, DOT, BOC-3)',
      'Startup Budget Planner',
      'Insurance Comparison Sheet',
      'Broker Setup Packet Template',
      'Carrier Packet Template',
      'Business Setup Checklist'
    ],
    audience: 'New Trucking Businesses'
  },
  {
    id: 'compliance',
    title: 'Compliance Packet',
    description: 'Stay DOT compliant and audit-ready with these essential documents.',
    icon: Shield,
    color: '#C0C0C0',
    price: 25,
    items: [
      'DOT Audit Checklist',
      'Driver Qualification File Checklist',
      'Drug & Alcohol Log Template',
      'Accident Report Template',
      'Safety Meeting Template',
      'Monthly Compliance Tracker',
      'Document Retention Checklist'
    ],
    audience: 'Fleet Managers'
  },
  {
    id: 'dispatch',
    title: 'Dispatch Packet',
    description: 'Everything dispatchers need to manage loads, carriers, and brokers.',
    icon: Headphones,
    color: '#D4AF37',
    price: 25,
    items: [
      'Dispatch Service Agreement',
      'Carrier Onboarding Packet',
      'Broker Call Script',
      'Rate Negotiation Script',
      'Dispatch Workflow Checklist',
      'Load Tracker Sheet',
      'Invoice Template',
      'Broker Contact Tracker'
    ],
    audience: 'Dispatchers'
  }
];

const BUNDLE_PRICE = 125;
const INDIVIDUAL_TOTAL = TEMPLATE_PACKETS.reduce((sum, p) => sum + p.price, 0);
const SAVINGS = INDIVIDUAL_TOTAL - BUNDLE_PRICE;

const ToolkitPage = () => {
  const [selectedPackets, setSelectedPackets] = useState([]);

  const togglePacket = (id) => {
    setSelectedPackets(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const getTotal = () => {
    if (selectedPackets.length === TEMPLATE_PACKETS.length) {
      return BUNDLE_PRICE;
    }
    return selectedPackets.length * 25;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="toolkit-page">
      <SEO 
        title="Queen B Resources | Premium Business Templates"
        description="Professional template packets for CDL students, owner operators, freight brokers, and dispatchers. Download ready-to-use business tools."
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-[#050505] border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <Package className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Digital Products</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase mb-4">
              Queen B <span className="text-[#D4AF37]">Resources</span>
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
              Premium business templates and resources for the trucking industry. 
              Ready to download, ready to use.
            </p>
          </div>

          {/* Pricing Summary */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mt-12">
            <div className="bg-[#121212] border border-white/10 px-8 py-4 text-center">
              <p className="text-white/50 text-sm uppercase tracking-wider mb-1">Individual Packets</p>
              <p className="text-white text-3xl font-bold">$25 <span className="text-white/50 text-lg font-normal">each</span></p>
            </div>
            <div className="text-white/30 text-2xl hidden md:block">or</div>
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/50 px-8 py-4 text-center relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 uppercase">
                Best Value
              </div>
              <p className="text-[#D4AF37]/70 text-sm uppercase tracking-wider mb-1">Complete Bundle</p>
              <p className="text-[#D4AF37] text-3xl font-bold">$125 <span className="text-[#D4AF37]/50 text-lg font-normal line-through ml-2">${INDIVIDUAL_TOTAL}</span></p>
              <p className="text-green-400 text-sm mt-1">Save ${SAVINGS} + Get 1 Packet FREE</p>
            </div>
          </div>
        </div>
      </section>

      {/* Template Packets Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-4">
              Choose Your Packets
            </h2>
            <p className="text-white/50">Select individual packets or get all 6 with the bundle discount</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEMPLATE_PACKETS.map((packet) => {
              const Icon = packet.icon;
              const isSelected = selectedPackets.includes(packet.id);
              
              return (
                <div 
                  key={packet.id}
                  className={`bg-[#121212] border ${isSelected ? 'border-[#D4AF37]' : 'border-white/10'} p-6 transition-all hover:border-white/30 cursor-pointer`}
                  onClick={() => togglePacket(packet.id)}
                  data-testid={`packet-${packet.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${packet.color}20`, border: `1px solid ${packet.color}40` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: packet.color }} />
                      </div>
                      <div>
                        <h3 className="text-white font-bold">{packet.title}</h3>
                        <p className="text-white/40 text-xs uppercase">{packet.audience}</p>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30'}`}>
                      {isSelected && <CheckCircle className="w-4 h-4 text-black" />}
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-sm mb-4">{packet.description}</p>

                  {/* Contents */}
                  <div className="space-y-2 mb-4">
                    <p className="text-white/40 text-xs uppercase tracking-wider">Includes:</p>
                    <ul className="space-y-1">
                      {packet.items.slice(0, 4).map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-white/70">
                          <FileText className="w-3 h-3 text-[#D4AF37]" />
                          {item}
                        </li>
                      ))}
                      {packet.items.length > 4 && (
                        <li className="text-[#D4AF37] text-sm">+{packet.items.length - 4} more templates</li>
                      )}
                    </ul>
                  </div>

                  {/* Price & Actions */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[#D4AF37] text-2xl font-bold">${packet.price}</span>
                      <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/30 hover:border-white/50'}`}>
                        {isSelected && <CheckCircle className="w-4 h-4 text-black" />}
                      </div>
                    </div>
                    <Link
                      to={`/toolkit/${packet.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full py-2 border border-[#D4AF37]/50 text-[#D4AF37] text-sm font-bold uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors flex items-center justify-center gap-2"
                      data-testid={`view-details-${packet.id}`}
                    >
                      <Eye className="w-4 h-4" />
                      View Details & Preview
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bundle CTA */}
          <div className="mt-12 bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-[#D4AF37]/10 border border-[#D4AF37]/30 p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-[#D4AF37]" />
                  <h3 className="text-white font-bold text-xl">Get the Complete Bundle</h3>
                </div>
                <p className="text-white/60">All 6 template packets for one low price. Save ${SAVINGS} instantly.</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-white/40 text-sm line-through">${INDIVIDUAL_TOTAL}</p>
                  <p className="text-[#D4AF37] text-3xl font-bold">${BUNDLE_PRICE}</p>
                </div>
                <Link
                  to="/toolkit/checkout?type=bundle"
                  className="px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors flex items-center gap-2"
                  data-testid="buy-bundle-btn"
                >
                  Buy Bundle
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Selected Items Checkout */}
          {selectedPackets.length > 0 && selectedPackets.length < TEMPLATE_PACKETS.length && (
            <div className="mt-8 bg-[#121212] border border-white/10 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-white font-bold">{selectedPackets.length} packet{selectedPackets.length > 1 ? 's' : ''} selected</p>
                <p className="text-white/50 text-sm">
                  {selectedPackets.length === 5 ? 'Add 1 more packet to get the bundle discount!' : `Select ${6 - selectedPackets.length} more for bundle pricing`}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-[#D4AF37] text-2xl font-bold">${getTotal()}</p>
                <Link
                  to={`/toolkit/checkout?packets=${selectedPackets.join(',')}`}
                  className="px-6 py-3 bg-white text-black font-bold uppercase tracking-wider hover:bg-white/90 transition-colors"
                  data-testid="checkout-selected-btn"
                >
                  Checkout
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-16 bg-[#050505] border-y border-white/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-4">
              What You Get
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <Download className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold mb-2">Instant Download</h3>
              <p className="text-white/50 text-sm">Get your templates immediately after purchase. No waiting.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold mb-2">Professional Quality</h3>
              <p className="text-white/50 text-sm">Clean, branded PDFs ready for immediate business use.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold mb-2">Lifetime Access</h3>
              <p className="text-white/50 text-sm">Access your templates anytime through The Hive dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-2">How do I access my templates after purchase?</h3>
              <p className="text-white/60 text-sm">You'll receive instant download links on the confirmation page and via email. You can also access them anytime through The Hive dashboard.</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-2">What format are the templates in?</h3>
              <p className="text-white/60 text-sm">All templates are provided as professional PDF files. Some include fillable fields for easy customization.</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-2">Can I use these templates for my business?</h3>
              <p className="text-white/60 text-sm">Yes! These templates are designed for professional business use. You can customize them with your own branding.</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-bold mb-2">Are refunds available?</h3>
              <p className="text-white/60 text-sm">Due to the digital nature of these products, all sales are final. Please review the packet contents before purchasing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#050505] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Ready to Level Up Your Business?
          </h2>
          <p className="text-white/60 mb-8">Get all 6 template packets and save ${SAVINGS}</p>
          <Link
            to="/toolkit/checkout?type=bundle"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
          >
            Get the Complete Bundle - ${BUNDLE_PRICE}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ToolkitPage;
